import { useState } from "react";

import { festivals } from "./api";

/**
 * Import d'une programmation depuis un tableur.
 *
 * L'aperçu n'est pas une politesse : cent concerts déversés sans être montrés,
 * ce sont cent concerts faux à reprendre un à un. Rien n'est écrit avant un
 * second geste, explicite.
 */

type Preview = {
  headers?: string[];
  columns?: Record<string, string>;
  issues: { line: number; message: string }[];
  performances?: { stage: string; artist: string; startsAt: string; endsAt: string }[];
  total?: number;
};

export function LineupImport({
  slug,
  onImported,
}: {
  slug: string;
  onImported: () => void;
}) {
  const [content, setContent] = useState("");
  const [defaultDay, setDefaultDay] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const body = () => ({
    content,
    ...(defaultDay ? { defaultDay } : {}),
  });

  const analyse = async () => {
    setBusy(true);
    setMessage(null);
    try {
      setPreview(await festivals.importLineup(slug, body()));
    } catch {
      setMessage("Fichier illisible.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const result = await festivals.importLineup(slug, { ...body(), dryRun: false });
      setMessage(`${result.created} concert(s) importé(s). Publiez pour les diffuser.`);
      setPreview(null);
      setContent("");
      onImported();
    } catch {
      setMessage("Import impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>Importer depuis un tableur</h2>
      <p className="muted">
        Déposez un fichier CSV, ou collez directement le contenu. Les colonnes sont
        reconnues automatiquement ; l'aperçu montre ce qui sera créé avant d'écrire quoi
        que ce soit.
      </p>

      <div className="row">
        <input
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            setContent(await file.text());
            setPreview(null);
          }}
        />
        <label style={{ minWidth: 200 }}>
          Journée par défaut
          <input
            type="date"
            value={defaultDay}
            onChange={(e) => setDefaultDay(e.target.value)}
          />
        </label>
      </div>

      <label>
        Contenu
        <textarea
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            setPreview(null);
          }}
          placeholder="Scène;Artiste;Jour;Début;Fin"
        />
      </label>

      <div className="row">
        <button onClick={() => void analyse()} disabled={busy || !content.trim()}>
          Analyser
        </button>
        <button
          className="primary"
          onClick={() => void confirm()}
          disabled={busy || !preview || (preview.total ?? 0) === 0}
        >
          Importer {preview?.total ? `${preview.total} concert(s)` : ""}
        </button>
      </div>

      {message ? <p className="muted">{message}</p> : null}

      {preview ? (
        <>
          <p className="muted">
            Colonnes reconnues :{" "}
            {Object.entries(preview.columns ?? {})
              .map(([field, header]) => `${field} → « ${header} »`)
              .join(", ") || "aucune"}
          </p>

          {preview.issues.length > 0 ? (
            <div className="error">
              <strong>{preview.issues.length} ligne(s) écartée(s)</strong>
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {preview.issues.slice(0, 10).map((issue) => (
                  <li key={issue.line}>
                    Ligne {issue.line} : {issue.message}
                  </li>
                ))}
              </ul>
              {preview.issues.length > 10 ? (
                <p className="muted">…et {preview.issues.length - 10} autre(s).</p>
              ) : null}
            </div>
          ) : null}

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Scène</th>
                  <th>Artiste</th>
                  <th>Début</th>
                  <th>Fin</th>
                </tr>
              </thead>
              <tbody>
                {(preview.performances ?? []).slice(0, 20).map((p, index) => (
                  <tr key={`${p.artist}-${index}`}>
                    <td>{p.stage}</td>
                    <td>{p.artist}</td>
                    <td>{new Date(p.startsAt).toLocaleString("fr-FR")}</td>
                    <td>{new Date(p.endsAt).toLocaleString("fr-FR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(preview.total ?? 0) > 20 ? (
            <p className="muted">Aperçu des 20 premières lignes sur {preview.total}.</p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
